import { union } from 'lodash';
import { create } from 'zustand';

import { IMetadataRuleData } from '@components/mapping-panel/components/RuleBuilder/MetadataRuleGroup/typed';
import { MappedEventTypes } from '@src/common/types/empTypes';

import { IRuleGroup } from '../components/RuleBuilder/RuleGroup/typed';
import { EMappingRuleType } from '../typed';
import { getEmptyRuleGroup, getInitialActiveIndices } from '../utils/initializeUtils';

export interface IItemActiveIndices {
  subAndIndex?: number;
  subOrIndex?: number;
  tagKey?: boolean;
  tagValue?: boolean;
}

export interface IMetadataItemActiveIndices {
  subAndIndex?: number;
  subOrIndex?: number;
}

export interface IActiveIndices {
  orIndex?: number;
  eventName?: boolean;
  metadataName?: boolean;
  ruleType: EMappingRuleType;
  ruleItems?: IItemActiveIndices[];
  metadataItems?: IMetadataItemActiveIndices[];
}

export interface IMappingRules extends IRuleGroup, IMetadataRuleData {}

export const EXISTING_RULES_NOTIFICATION = 'The following are the existing mapping rules';
export const EXISTING_RULES_NOTIFICATION_EDITED =
  'The following are the existing mapping rules (edited)';

interface IMappingPanelState {
  needRefetchQueryKeys: string[];
  mappedEvent: MappedEventTypes | undefined;
  predefinedMappedEvent: MappedEventTypes | undefined;
  eventCategory: string | undefined;
  mappedName: string | undefined;
  mappingRules: IMappingRules[];
  activeIndices: IActiveIndices;
  mappingPanelOpen: boolean;
  predefinedMappingRules: IMappingRules[];
  existingRulesNotification: string;
  setMappedEvent: (mappedEvent: MappedEventTypes | undefined) => void;
  setPredefinedMappedEvent: (predefinedMappedEvent: MappedEventTypes | undefined) => void;
  setEventCategory: (eventCategory: string | undefined) => void;
  setMappedName: (mappedName: string | undefined) => void;
  setMappingRules: (mappingRules: IMappingRules[]) => void;
  setActiveIndices: (ActiveIndices: IActiveIndices) => void;
  setMappingPanelOpen: (mappingPanelOpen: boolean) => void;
  setPredefinedMappingRules: (predefinedMappingRules: IRuleGroup[]) => void;
  setExistingRulesNotification: (existingRulesNotification: string) => void;
  setNeedRefetchQueryKeys: (
    newNeedRefetchQueryKeys: string[] | string,
    action?: 'add' | 'delete',
  ) => void;
  resetMappingStore: () => void;
}

export const useMappingPanelStore = create<IMappingPanelState>((set, get) => ({
  needRefetchQueryKeys: [],
  mappedEvent: undefined,
  predefinedMappedEvent: undefined,
  eventCategory: undefined,
  mappedName: undefined,
  mappingPanelOpen: false,
  mappingRules: [getEmptyRuleGroup()],
  activeIndices: getInitialActiveIndices(),
  predefinedMappingRules: [],
  existingRulesNotification: EXISTING_RULES_NOTIFICATION,
  setMappedEvent: (mappedEvent: MappedEventTypes | undefined) => set(() => ({ mappedEvent })),
  setPredefinedMappedEvent: (predefinedMappedEvent: MappedEventTypes | undefined) =>
    set(() => ({ predefinedMappedEvent })),
  setEventCategory: (eventCategory: string | undefined) => set(() => ({ eventCategory })),
  setMappedName: (mappedName: string | undefined) => set(() => ({ mappedName })),
  setMappingRules: (mappingRules: IMappingRules[]) => set(() => ({ mappingRules })),
  setActiveIndices: (activeIndices: IActiveIndices) => set(() => ({ activeIndices })),
  setMappingPanelOpen: (mappingPanelOpen: boolean) => set(() => ({ mappingPanelOpen })),
  setPredefinedMappingRules: (predefinedMappingRules: IRuleGroup[]) =>
    set(() => ({ predefinedMappingRules })),
  setExistingRulesNotification: (existingRulesNotification: string) =>
    set(() => ({ existingRulesNotification })),
  setNeedRefetchQueryKeys: (newNeedRefetchQueryKeys, action = 'add') =>
    set(() => {
      const needRefetchQueryKeys = get().needRefetchQueryKeys;
      switch (action) {
        case 'delete': {
          let filterNeedRefetchQueryKeys;
          if (Array.isArray(newNeedRefetchQueryKeys)) {
            filterNeedRefetchQueryKeys = needRefetchQueryKeys.filter(
              (item) => !newNeedRefetchQueryKeys.includes(item),
            );
          } else {
            filterNeedRefetchQueryKeys = needRefetchQueryKeys.filter(
              (item) => item !== newNeedRefetchQueryKeys,
            );
          }
          return { needRefetchQueryKeys: filterNeedRefetchQueryKeys };
        }
        case 'add':
        default:
          if (Array.isArray(newNeedRefetchQueryKeys)) {
            const unionNeedRefetchQueryKeys = union(needRefetchQueryKeys, newNeedRefetchQueryKeys);
            return { needRefetchQueryKeys: unionNeedRefetchQueryKeys };
          } else {
            needRefetchQueryKeys.push(newNeedRefetchQueryKeys);
            return { needRefetchQueryKeys };
          }
      }
    }),
  resetMappingStore: () =>
    set(() => ({
      needRefetchQueryKeys: [],
      mappedEvent: undefined,
      predefinedMappedEvent: undefined,
      eventCategory: undefined,
      mappedName: undefined,
      mappingPanelOpen: false,
      mappingRules: [getEmptyRuleGroup()],
      activeIndices: getInitialActiveIndices(),
      predefinedMappingRules: [],
      existingRulesNotification: EXISTING_RULES_NOTIFICATION,
    })),
}));
